<script setup lang="ts">
import { computed } from 'vue'
import { Bell } from '@/shared/icons'
import { Card } from '@/shared/ui/Card'
import { Icon } from '@/shared/ui/Icon'
import { Inline } from '@/shared/ui/Inline'
import { Stack } from '@/shared/ui/Stack'

import type { UserNote } from '../model/types'

const props = defineProps<{ note: UserNote }>()

// Both fields arrive as full timestamps, so `isoToDayFirst` cannot read them — it splits
// a bare `YYYY-MM-DD` on its dashes. `en-GB` is day-first, which is what that helper
// writes everywhere else in the tree.
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
})

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
})

const createdAt = computed(() =>
  DATE_TIME.format(new Date(props.note.createdAt))
)

const notifyAt = computed(() =>
  props.note.notifyAt ? DATE.format(new Date(props.note.notifyAt)) : ''
)
</script>

<template>
  <Card shadow="hover">
    <Stack :gap="8">
      <Inline :gap="12" align="center" justify="between">
        <b>{{ note.author }}</b>
        <Inline v-if="notifyAt" :gap="4" align="center">
          <Icon><Bell /></Icon>
          {{ notifyAt }}
        </Inline>
        <span class="user-note-card__date">{{ createdAt }}</span>
      </Inline>
      <div class="user-note-card__text">{{ note.text }}</div>
    </Stack>
  </Card>
</template>

<style lang="scss" scoped>
.user-note-card__date {
  color: var(--el-text-color-secondary);
}

// The note is typed into a textarea, so its line breaks are the only structure it has.
.user-note-card__text {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
